if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;

        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.buttons = Array.from(this.querySelectorAll('[type="submit"]'));
        this.submitButton = this.querySelector('[data-submit-action="add"]') || this.buttons[0];
        this.submitButtonText = this.submitButton?.querySelector('span');

        if (document.querySelector('cart-drawer')) {
          this.buttons.forEach((button) => button.setAttribute('aria-haspopup', 'dialog'));
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.error = false;
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();

        const clickedButton = evt.submitter || this.submitButton;
        const submitAction = clickedButton?.dataset.submitAction || 'add';
        const isBuyNow = submitAction === 'buy-now';

        if (clickedButton?.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.error = false;

        this.setButtonsLoadingState(true, clickedButton);

        const formData = new FormData(this.form);
        const addonItems = this.getAddonItems();

        let config;

        if (addonItems.length > 0) {
          config = this.buildMultiItemConfig(formData, addonItems);
        } else {
          config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }

          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description || 'Something went wrong. Please try again.');

              const soldOutMessage = clickedButton?.querySelector('.sold-out-message');
              if (soldOutMessage) {
                clickedButton.setAttribute('aria-disabled', true);
                const normalText = clickedButton.querySelector('span:not(.sold-out-message)');
                normalText?.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }

              this.error = true;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            }

            this.error = false;

            if (isBuyNow) {
              window.location.href = '/checkout';
              return;
            }

            const quickAddModal = this.closest('quick-add-modal');

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    if (this.cart) {
                      CartPerformance.measure('add:paint-updated-sections', () => {
                        this.cart.renderContents(response);
                      });
                    } else {
                      window.location.href = window.routes.cart_url;
                    }
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              if (this.cart) {
                CartPerformance.measure('add:paint-updated-sections', () => {
                  this.cart.renderContents(response);
                });
              } else {
                window.location.href = window.routes.cart_url;
              }
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage('Something went wrong. Please try again.');
          })
          .finally(() => {
            this.setButtonsLoadingState(false, clickedButton);

            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      setButtonsLoadingState(isLoading, activeButton = null) {
        this.buttons.forEach((button) => {
          const spinner = button.querySelector('.loading__spinner');

          if (isLoading) {
            button.setAttribute('aria-disabled', 'true');
            button.classList.toggle('loading', button === activeButton);
            if (spinner) {
              spinner.classList.toggle('hidden', button !== activeButton);
            }
          } else {
            if (!this.error) {
              button.removeAttribute('aria-disabled');
            }
            button.classList.remove('loading');
            if (spinner) {
              spinner.classList.add('hidden');
            }
          }
        });
      }

      getAddonCalculator() {
        return (
          this.closest('product-info')?.querySelector(
            `addon-price-calculator[data-section-id="${this.dataset.sectionId}"]`
          ) || null
        );
      }

      getAddonItems() {
        const calculator = this.getAddonCalculator();
        if (!calculator) return [];

        const rows = calculator.querySelectorAll('[data-addon-row]');
        const items = [];

        rows.forEach((row) => {
          const checkbox = row.querySelector('[data-addon-checkbox]');
          const qtyInput = row.querySelector('[data-addon-qty]');
          const variantId = String(row.dataset.variantId || '').trim();
          const addonName = String(row.dataset.addonName || 'Add-on').trim();
          const qty = parseInt(qtyInput?.value || '0', 10) || 0;
          const unitPrice = String(row.dataset.unitPrice || '').trim();

          if (!checkbox?.checked || !variantId || qty <= 0) return;

          items.push({
            id: variantId,
            quantity: qty,
            properties: {
              '_Linked service': document.title,
              '_Add-on name': addonName,
              '_Displayed unit price': unitPrice ? unitPrice : '',
            },
          });
        });

        return items;
      }

      getExternalFormProperties() {
        const calculator = this.getAddonCalculator();
        const properties = {};

        if (!calculator) return properties;

        calculator.querySelectorAll('input[name^="properties["]:not([disabled])').forEach((input) => {
          const key = input.name.replace(/^properties\[/, '').replace(/\]$/, '');
          if (String(input.value).trim() !== '') properties[key] = input.value;
        });

        return properties;
      }

      getFormProperties(formData) {
        const properties = {};

        for (const [key, value] of formData.entries()) {
          if (key.startsWith('properties[')) {
            const propertyName = key.replace(/^properties\[/, '').replace(/\]$/, '');
            if (String(value).trim() !== '') {
              properties[propertyName] = value;
            }
          }
        }

        return properties;
      }

      buildMainItem(formData) {
        const mainItem = {
          id: formData.get('id'),
          quantity: parseInt(formData.get('quantity') || '1', 10) || 1,
        };

        const properties = {
          ...this.getFormProperties(formData),
          ...this.getExternalFormProperties(),
        };

        if (Object.keys(properties).length > 0) {
          mainItem.properties = properties;
        }

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) {
          mainItem.selling_plan = sellingPlan;
        }

        return mainItem;
      }

      buildMultiItemConfig(formData, addonItems) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.headers['Content-Type'] = 'application/json';

        const payload = {
          items: [this.buildMainItem(formData), ...addonItems],
        };

        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = JSON.stringify(payload);
        return config;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        this.buttons.forEach((button) => {
          if (disable) {
            button.setAttribute('disabled', 'disabled');
          } else {
            button.removeAttribute('disabled');
          }
        });

        if (this.submitButtonText) {
          if (disable && text) {
            this.submitButtonText.textContent = text;
          } else if (!disable) {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}